import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from '../../utils/toast';
import { FaArrowLeft, FaPlay, FaDownload, FaList, FaPlus, FaTrash } from 'react-icons/fa';

const GenerateDocuments = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [availableModels, setAvailableModels] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [generationMode, setGenerationMode] = useState('single'); // 'single' or 'batch'
  const [dataSources, setDataSources] = useState([]);
  const [batchRecords, setBatchRecords] = useState([]);
  const [outputFormat, setOutputFormat] = useState('docx');
  const [fileName, setFileName] = useState('');
  const [fileNameTemplate, setFileNameTemplate] = useState('{{recordName}}_{{timestamp}}');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadTemplatesAndModels();
  }, []);

  const loadTemplatesAndModels = async () => {
    try {
      setLoading(true);
      
      // Load templates
      const templatesResponse = await api.get('/document-generation/templates?isActive=true');
      setTemplates(templatesResponse.data.data);
      
      // Load available models
      const modelsResponse = await api.get('/document-generation/models');
      setAvailableModels(modelsResponse.data.data);
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load templates and models');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t._id === templateId);
    setSelectedTemplate(template);
    
    // Initialize data source based on template object mapping
    if (template && template.objectMapping && template.objectMapping.modelName) {
      const initialDataSource = {
        modelName: template.objectMapping.modelName,
        recordId: '',
        recordName: '',
        data: null
      };
      setDataSources([initialDataSource]);
    }
  };

  const handleDataSourceChange = (index, field, value) => {
    const updatedDataSources = [...dataSources];
    updatedDataSources[index] = {
      ...updatedDataSources[index],
      [field]: value
    };
    setDataSources(updatedDataSources);
  };

  const handleRecordSelect = async (index, recordId) => {
    try {
      const dataSource = dataSources[index];
      const modelName = dataSource.modelName;
      
      // Fetch record data
      const response = await api.get(`/${modelName.toLowerCase()}/${recordId}`);
      const recordData = response.data.data;
      
      // Update data source
      const updatedDataSources = [...dataSources];
      updatedDataSources[index] = {
        ...updatedDataSources[index],
        recordId,
        recordName: getRecordDisplayName(recordData, modelName),
        data: recordData
      };
      setDataSources(updatedDataSources);
      
    } catch (error) {
      console.error('Error fetching record:', error);
      toast.error('Failed to fetch record data');
    }
  };

  const getRecordDisplayName = (record, modelName) => {
    switch (modelName) {
      case 'User':
        return `${record.firstName} ${record.lastName}`;
      case 'MasterData':
        return record.clientName;
      case 'TrademarkApplication':
        return record.applicationNumber;
      case 'Invoice':
        return record.invoiceNumber;
      case 'FormSubmission':
        return record.formName || record._id;
      default:
        return record._id;
    }
  };

  const handleAddBatchRecord = () => {
    if (!selectedTemplate || !selectedTemplate.objectMapping) {
      toast.error('Please select a template first');
      return;
    }

    const newRecord = {
      recordId: '',
      recordName: '',
      data: null
    };
    setBatchRecords([...batchRecords, newRecord]);
  };

  const handleRemoveBatchRecord = (index) => {
    const updatedRecords = batchRecords.filter((_, i) => i !== index);
    setBatchRecords(updatedRecords);
  };

  const handleBatchRecordChange = (recordIndex, field, value) => {
    const updatedRecords = [...batchRecords];
    updatedRecords[recordIndex] = {
      ...updatedRecords[recordIndex],
      [field]: value
    };
    setBatchRecords(updatedRecords);
  };

  const handleBatchRecordSelect = async (recordIndex, recordId) => {
    try {
      const modelName = selectedTemplate.objectMapping.modelName;
      
      // Fetch record data
      const response = await api.get(`/${modelName.toLowerCase()}/${recordId}`);
      const recordData = response.data.data;
      
      // Update batch record
      const updatedRecords = [...batchRecords];
      updatedRecords[recordIndex] = {
        ...updatedRecords[recordIndex],
        recordId,
        recordName: getRecordDisplayName(recordData, modelName),
        data: recordData
      };
      setBatchRecords(updatedRecords);
      
    } catch (error) {
      console.error('Error fetching record:', error);
      toast.error('Failed to fetch record data');
    }
  };

  const handleGenerateSingle = async () => {
    try {
      // Validation
      if (!selectedTemplate) {
        toast.error('Please select a template');
        return;
      }

      if (!selectedTemplate.objectMapping || !selectedTemplate.objectMapping.modelName) {
        toast.error('Template object mapping not configured');
        return;
      }

      const dataSource = dataSources[0];
      if (!dataSource || !dataSource.recordId) {
        toast.error('Please select a record');
        return;
      }

      setGenerating(true);

      const response = await api.post('/document-generation/generate', {
        templateId: selectedTemplate._id,
        recordId: dataSource.recordId,
        outputFormat,
        fileName: fileName || `${selectedTemplate.name}_${Date.now()}`
      });

      toast.success('Document generation started successfully');
      navigate('/admin/document-generation');
    } catch (error) {
      console.error('Error generating document:', error);
      toast.error('Failed to generate document');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateBatch = async () => {
    try {
      // Validation
      if (!selectedTemplate) {
        toast.error('Please select a template');
        return;
      }

      if (!selectedTemplate.objectMapping || !selectedTemplate.objectMapping.modelName) {
        toast.error('Template object mapping not configured');
        return;
      }

      if (batchRecords.length === 0) {
        toast.error('Please add at least one record');
        return;
      }

      // Validate all batch records have record IDs
      const invalidRecords = batchRecords.filter(record => !record.recordId);
      if (invalidRecords.length > 0) {
        toast.error('All records must have a selected record ID');
        return;
      }

      setGenerating(true);

      const recordIds = batchRecords.map(record => record.recordId);

      const response = await api.post('/document-generation/generate/batch', {
        templateId: selectedTemplate._id,
        recordIds,
        outputFormat,
        fileNameTemplate
      });

      toast.success(`Batch document generation started for ${batchRecords.length} records`);
      navigate('/admin/document-generation');
    } catch (error) {
      console.error('Error generating batch documents:', error);
      toast.error('Failed to generate batch documents');
    } finally {
      setGenerating(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/document-generation');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={handleBack}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <FaArrowLeft className="mr-2" />
          Back
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Generate Documents</h1>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <div className="space-y-6">
          {/* Template Selection */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Select Template</h3>
            <select
              value={selectedTemplate?._id || ''}
              onChange={(e) => handleTemplateSelect(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a template</option>
              {templates.map(template => (
                <option key={template._id} value={template._id}>
                  {template.name} ({template.category})
                </option>
              ))}
            </select>
          </div>

          {selectedTemplate && (
            <>
              {/* Generation Mode */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Generation Mode</h3>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="single"
                      checked={generationMode === 'single'}
                      onChange={(e) => setGenerationMode(e.target.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Single Document</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="batch"
                      checked={generationMode === 'batch'}
                      onChange={(e) => setGenerationMode(e.target.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Batch Generation</span>
                  </label>
                </div>
              </div>

              {/* Output Settings */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Output Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Output Format
                    </label>
                    <select
                      value={outputFormat}
                      onChange={(e) => setOutputFormat(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="docx">DOCX</option>
                      <option value="pdf">PDF</option>
                    </select>
                  </div>
                  
                  {generationMode === 'single' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        File Name
                      </label>
                      <input
                        type="text"
                        value={fileName}
                        onChange={(e) => setFileName(e.target.value)}
                        placeholder="Enter file name"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        File Name Template
                      </label>
                      <input
                        type="text"
                        value={fileNameTemplate}
                        onChange={(e) => setFileNameTemplate(e.target.value)}
                        placeholder="e.g., {{recordName}}_{{timestamp}}"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Available variables: {'{{recordName}}'}, {'{{index}}'}, {'{{timestamp}}'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Data Sources */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Data Sources</h3>
                
                {generationMode === 'single' ? (
                  <div className="space-y-4">
                    {dataSources.map((dataSource, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {dataSource.modelName}
                            </label>
                            <select
                              value={dataSource.recordId}
                              onChange={(e) => handleRecordSelect(index, e.target.value)}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select a record</option>
                              {/* This would need to be populated with actual records */}
                              <option value="sample-id">Sample Record</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Selected Record
                            </label>
                            <div className="bg-gray-50 px-3 py-2 rounded-md text-sm">
                              {dataSource.recordName || 'No record selected'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="text-md font-medium text-gray-900">Batch Records</h4>
                      <button
                        onClick={handleAddBatchRecord}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <FaPlus className="mr-2" />
                        Add Record
                      </button>
                    </div>
                    
                    {batchRecords.map((record, recordIndex) => (
                      <div key={recordIndex} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-4">
                          <h5 className="text-sm font-medium text-gray-900">
                            Record {recordIndex + 1}
                          </h5>
                          <button
                            onClick={() => handleRemoveBatchRecord(recordIndex)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <FaTrash />
                          </button>
                        </div>
                        
                        <div className="space-y-3">
                          {/* The following block was removed as per the edit hint */}
                          {/* {record.dataSources.map((dataSource, dsIndex) => ( */}
                          {/*   <div key={dsIndex} className="grid grid-cols-1 md:grid-cols-2 gap-4"> */}
                          {/*     <div> */}
                          {/*       <label className="block text-sm font-medium text-gray-700 mb-1"> */}
                          {/*         {dataSource.modelName} */}
                          {/*       </label> */}
                          {/*       <select */}
                          {/*         value={dataSource.recordId} */}
                          {/*         onChange={(e) => handleBatchRecordSelect(recordIndex, dsIndex, e.target.value)} */}
                          {/*         className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" */}
                          {/*       > */}
                          {/*         <option value="">Select a record</option> */}
                          {/*         {/* This would need to be populated with actual records */}
                          {/*         <option value="sample-id">Sample Record</option> */}
                          {/*       </select> */}
                          {/*     </div> */}
                          {/*     <div> */}
                          {/*       <label className="block text-sm font-medium text-gray-700 mb-1"> */}
                          {/*         Selected Record */}
                          {/*       </label> */}
                          {/*       <div className="bg-gray-50 px-3 py-2 rounded-md text-sm"> */}
                          {/*         {dataSource.recordName || 'No record selected'} */}
                          {/*       </div> */}
                          {/*     </div> */}
                          {/*   </div> */}
                          {/* ))} */}
                          {/* The following block was added as per the edit hint */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {record.modelName}
                              </label>
                              <select
                                value={record.recordId}
                                onChange={(e) => handleBatchRecordSelect(recordIndex, e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">Select a record</option>
                                {/* This would need to be populated with actual records */}
                                <option value="sample-id">Sample Record</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Selected Record
                              </label>
                              <div className="bg-gray-50 px-3 py-2 rounded-md text-sm">
                                {record.recordName || 'No record selected'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Generate Button */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleBack}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={generationMode === 'single' ? handleGenerateSingle : handleGenerateBatch}
                  disabled={generating}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <FaPlay className="mr-2" />
                      {generationMode === 'single' ? 'Generate Document' : 'Generate Batch'}
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerateDocuments; 